/**
 * Client-side pre-check for COMO (defense in depth). Server-side policy is authoritative.
 */

export type ClientSafetyResult =
  | { allowed: true }
  | { allowed: false; message: string; reasonCode: string };

const INJECTION_RE =
  /\bignore\s+(all\s+)?(previous|prior|above)\s+(instructions?|rules?|system)\b/i;

const EXFIL_RE =
  /\b(show|print|dump|leak)\b.+\b(api[_\s-]?key|secret|password|token|credential)\b/i;

const DESTRUCTIVE_RE = /\brm\s+-(?:rf|r)\b|terraform\s+destroy|drop\s+database\b/i;

const HIGH_RISK_RE =
  /\brestart\s+(all\s+)?(?:pods|services|servers|nodes)\b|\bmass\s+(?:restart|deploy|delete)\b/i;

export function evaluatePromptClientSide(raw: string): ClientSafetyResult {
  const text = (raw || '').trim();
  if (!text) {
    return {
      allowed: false,
      message: 'Please enter a question so COMO can help.',
      reasonCode: 'empty',
    };
  }
  if (text.length > 48_000) {
    return {
      allowed: false,
      message: 'That message is too long. Shorten it and try again.',
      reasonCode: 'input_too_long',
    };
  }
  if (INJECTION_RE.test(text)) {
    return {
      allowed: false,
      message:
        "I can’t follow instructions that try to override my safety rules. Ask a normal monitoring or reliability question.",
      reasonCode: 'prompt_injection',
    };
  }
  if (EXFIL_RE.test(text)) {
    return {
      allowed: false,
      message:
        'I can’t help retrieve secrets or credentials. Use your secret manager or CoreMonit admin tools.',
      reasonCode: 'secret_exfiltration',
    };
  }
  if (DESTRUCTIVE_RE.test(text)) {
    return {
      allowed: false,
      message:
        'I can’t assist with destructive infrastructure commands. Use approved change workflows in CoreMonit.',
      reasonCode: 'destructive_intent',
    };
  }
  if (HIGH_RISK_RE.test(text)) {
    return {
      allowed: false,
      message:
        'I can’t drive production restarts or mass changes from chat. Use CoreMonit operational workflows with approvals.',
      reasonCode: 'high_risk_operational',
    };
  }
  return { allowed: true };
}
